<template>
  <div class="plan-card-wrapper">
    <div class="plan-card">
      <span v-if="!banner && sale" class="sale label-sm">Sale</span>
      <span v-if="banner" :class="['label', $store.state.banners.active ? $store.state.banners.active.typeName : '']">{{ label }}</span>
      <!-- <span v-if="$config.platform === 'fa' && $store.getters['subscriptions/annual'].source === 'platform'" class="label"> Special Offer</span> -->
      <h3 class="h5" :class="[plan.id === 2 ? 'dark' : '']">
        Premium {{ plan.shortTitle }}
      </h3>
      <div class="content">
        <span class="price" :class="{ discount: sale }">
          <template v-if="1">
            <span v-if="billed.originalPriceMonthly && billed.originalPriceMonthly !== billedPerMonth.regular" class="original-monthly">
              {{ billed.originalPriceMonthly | formatCurrency(currency, { minimumFractionDigits: 0 }) }}
            </span>
            <span class="regular-price">
              {{ billedPerMonth.discount || billedPerMonth.regular | formatCurrency(currency, { minimumFractionDigits: 0, dollarVariation: false }) }}
              <span>
                {{ '/month' | formatMonthPriceCurrency(currency) }}
              </span>
            </span>
          </template>
          <span v-else class="spin-loader">
            <IconSync class="spin" />
          </span>
        </span>
        <div class="billed">
          {{ billed.text }}
          <span v-if="billed.saving" :class="['save-label', $store.state.banners.active ? $store.state.banners.active.typeName : '']">Save {{ billed.saving }}%</span>
        </div>
      </div>

      <CounterWithDiscount
        v-if="showCounter"
        counter-class="filled"
        :utc-end-date="banner.countDownDate"
      />
      <div class="footer">
        <button
          v-if="$store.state.campaign === 'top-finance-training'" class="btn-primary" :class="{ 'annual': plan.slug === 'annual' }"
          @click="handleClick"
        >
          {{ buttonLabel }}
        </button>
        <button
          v-else
          :class="[
            plan.source !== 'referral' && $config.platform === 'ds' ? 'btn-primary-purple-dark' : 'btn-primary',
            $store.state.banners.active ? $store.state.banners.active.typeName : '',
            campaign]"
          @click="handleClick"
        >
          {{ buttonLabel }}
        </button>
        <p class="see-all">
          <nuxt-link to="/pricing/">
            See All Plans
          </nuxt-link>
        </p>
      </div>
      <template v-if="$config.platform === 'ds' && $config.campaign === 'most-wanted-2024'">
        <div class="stamp">
          <img :src="require(`../assets/images/campaigns/${$config.platform}-mw-hero-stamp.webp`)">
        </div>
        <img v-if="campaign === 'most-wanted-2024'" class="extend-stamp" :src="require(`../assets/images/campaigns/${$config.platform}-mw-extend.webp`)">
      </template>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import IconSync from '../assets/svg/icon-sync.svg?vue-component';
import CounterWithDiscount from '../CounterWithDiscount.vue';

export default {
  components: { IconSync, CounterWithDiscount },
  props: {
    plan: { type: Object, required: true },
    campaign: { type: String, default: '', required: false },
  },
  computed: {
    ...mapState('subscriptions', ['currency']),
    ...mapGetters('banners', ['promoOrTrigger']),
    banner() {
      if (this.promoOrTrigger && this.$config.campaign !== 'free') {
        return this.promoOrTrigger;
      }
      return false;
    },
    offer() {
      return this.$store.getters['subscriptions/all'].find((o) => o.expiration) || {};
    },
    offerEnd() {
      return this.banner.endDate || this.offer.expiration;
    },
    showCounter() {
      return (this.offer.id && this.offer.source === 'benefit' && this.offer.id === this.plan.id)
        || this.banner.active
        || this.campaign;
    },
    teachable() {
      return this.$store.getters['auth/user'].activeSubscriptionProvider === 'teachable';
    },
    sale() {
      return this.plan.discountPrice < this.plan.price && !this.plan.source;
    },
    label() {
      if (['transform-your-future-2024', 'most-wanted-2024', 'future-proof-2024', 'fast-track-2024'].includes(this.campaign)) {
        return 'Limited-Time Offer';
      }

      const banner = this.$store.state.banners.active;
      const labels = {
        1: 'Special Offer',
        2: 'Special Offer',
        3: 'Limited Offer',
        4: 'Refer a Friend',
        5: 'Loyalty Offer',
      };
      if (banner) {
        return labels[banner.type];
      }
      return '';
    },
    buttonLabel() {
      if (['transform-your-future-2024', 'future-proof-2024', 'fast-track-2024'].includes(this.campaign)) {
        return 'Upgrade';
      }

      if (this.billed.saving) {
        return `Get ${this.billed.saving}% Off`;
      }

      return 'Upgrade Now';
    },
    billed() {
      const { id, price, discountPrice } = this.plan;

      const monthly = this.$store.getters['subscriptions/monthly'];
      const quarterly = this.$store.getters['subscriptions/quarterly'];
      const annual = this.$store.getters['subscriptions/annual'];

      if (id === monthly.id) {
        return { text: 'Billed once a month' };
      }

      if (id === quarterly.id) {
        const originalPrice = monthly.price * 3;
        const saving = (originalPrice ? Math.round((1 - ((discountPrice || price) / originalPrice)) * 100) : 0) || 0;

        return {
          text: 'Billed quarterly',
          originalPriceMonthly: monthly.price,
          saving,
        };
      }

      if (id === annual.id) {
        const originalPrice = monthly.price * 12;
        const saving = (originalPrice ? Math.round((1 - ((discountPrice || price) / originalPrice)) * 100) : 0);

        return {
          text: 'Billed annually',
          originalPriceMonthly: monthly.price,
          saving: saving === 19 ? 20 : saving,
        };
      }

      return { text: 'Try learning with our courses for free' };
    },
    billedPerMonth() {
      const { id, price, discountPrice = 0 } = this.plan;

      let billedPerMonth = 0;
      let billedPerMonthDiscount = 0;
      if (id === this.$store.getters['subscriptions/monthly'].id) {
        billedPerMonth = price;
        billedPerMonthDiscount = discountPrice;
      } else if (id === this.$store.getters['subscriptions/quarterly'].id) {
        billedPerMonth = price / 3;
        billedPerMonthDiscount = discountPrice / 3;
      } else if (id === this.$store.getters['subscriptions/annual'].id) {
        billedPerMonth = price / 12;
        billedPerMonthDiscount = discountPrice / 12;
      }

      return {
        regular: Number.isInteger(billedPerMonth) ? billedPerMonth : billedPerMonth.toFixed(2),
        discount: Number.isInteger(billedPerMonthDiscount) ? billedPerMonthDiscount : billedPerMonthDiscount.toFixed(2),
      };
    },
  },
  // watch: {
  //   offer: {
  //     handler(v) {
  //       if (v.discountPrice !== v.price) {
  //         this.startCountdown();
  //       }
  //     },
  //     deep: true,
  //   },
  //   banner(v) {
  //     if (v.active) {
  //       this.startCountdown();
  //     }
  //   },
  // },

  // mounted() {
  //   if (this.offer.id || this.banner.active) {
  //     this.startCountdown();
  //   }
  // },
  methods: {
    handleClick() {
      if (this.teachable) {
        return this.$router.push('/settings/subscription/');
      }

      if (this.plan.name) {
        this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...this.plan, planTitle: this.plan.title, currency: this.plan.currency.value } });

        if (this.$store.getters['auth/user'].activeSubscriptionProvider === 'fastspring') {
          this.$store.dispatch('subscriptions/checkout', { plan: this.plan.name });
        } else {
          window.location.href = `${this.$config.siteUrl}/checkout/?plan=${this.plan.shortTitle.toLowerCase()}${this.plan.coupon ? `&coupon=${this.plan.coupon}` : ''}`;
        }
      }

      return this.$emit('subscribe');
    },
    // startCountdown() {
    //   if (this.banner.interval || !this.showCounter) {
    //     return;
    //   }

    //   const clearCountdown = this.banner.startCountdown({
    //     clearCallback: () => {
    //       this.$store.dispatch('subscriptions/fetchPrices', true);
    //     },
    //   });

    //   this.$once('hook:beforeDestroy', () => clearCountdown());
    // },
  },
};
</script>

<style lang="scss">
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.plan-card-wrapper {
  position: relative;
  max-width: 280px;
  margin-top: var(--spacing-24);
  margin-bottom: var(--spacing-24);

  .plan-card {
    padding: var(--spacing-32);
    padding-bottom: var(--spacing-24);
    background-color: var(--main-light);
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

    .spin-loader {
      height: 46px;
    }

    .spin {
      width: 46px;
      height: 46px;
      animation: spin 1.6s linear infinite;
    }

    .label {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      height: 33px;
      top: -16px;
      left: 50%;
      transform: translate(-50%);
      font-weight: 600;
      width: 148px;
      color: var(--main-light);

      &.trigger {
        background: var(--main-red);
      }

      &.collection {
        width: 170px;
        background: var(--main-purple);
      }

      &.referral {
        color: #10141C;
        background: var(--light-purple);
      }

      &.loyalty {
        width: 128px;
        background: var(--second-platform-accent);
      }
    }

    .sale {
      position: absolute;
      top: 5px;
      right: 5px;
      background-color: var(--main-purple);
      color: var(--main-light);
      border-radius: 3px;
      font-weight: 600;
      width: 48px;
    }

    .h5 {
      margin-bottom: var(--spacing-12);
      color: var(--main-light-shade-4);
      font-weight: 600;

      &.dark {
        color: var(--main-dark-light-2);
      }
    }

    .content {
      line-height: 45px;
      color: var(--main-dark-shade-0);

      .price {
        position: relative;
        font-size: 38px;
        font-weight: 600;
        white-space: nowrap;

        .spin {
          path {
            fill: var(--accent);
          }
        }

        .original-monthly {
          position: absolute;
          top: -26px;
          left: 0;
          font-size: 14px;
          font-weight: 500;

          &:before {
            content: '';
            top: 50%;
            left: 0;
            right: 0;
            position: absolute;
            height: 1px;
            width: 100%;
            background-color: var(--main-dark-light-4);
          }
        }

        .regular-price {
          span {
            font-size: 14px;
            margin-left: -6px;
            font-weight: 400;
          }
        }
      }

      .billed {
        display: flex;
        align-items: center;
        margin-bottom: var(--spacing-16);
        color: var(--main-dark-light-4);
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        height: 24px;

        .save-label {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #56BFC7;
          font-weight: 600;
          background: rgba(86, 191, 199, 0.2);
          border-radius: 3px;
          margin-left: var(--spacing-8);
          height: 24px;
          width: 80px;

          &.trigger,
          &.collection,
          &.referral {
            color: var(--main-purple-dark-1);
            background: var(--main-purple-light-4);
          }
        }
      }
    }

    .counter {
      margin-bottom: var(--spacing-16);
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: flex-start;

      .square {
        width: 48px;
        height: 52px;
        border-radius: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        padding: 7px 14px;
        border: 1px solid var(--main-light-shade-3);

        &:not(:last-of-type) {
          margin-right: var(--spacing-8);
        }

        span {
          color: #6B738B;

          &.number {
            font-size: 24px;
            line-height: 110%;
          }

          &.display-text {
            color: #6B738B;
            font-weight: 500;
            font-size: 9px;
            line-height: 14px;
          }
        }
      }
    }

    .footer {
      button {
        width: 216px;
        height: 40px;
        padding: 0;

        &.trigger,
        &.referral {
          background: var(--main-purple-dark-1);
          color: var(--main-light);

          &:hover {
            background-color: var(--main-purple-dark-3);
          }

          &:active {
            background-color: var(--main-purple-dark-4);
          }
        }

        &.collection {
          cursor: pointer;
          border: none;
          background: var(--main-yellow);
          color: var(--main-dark);
        }

        &.loyalty {
          border: none;
          background: var(--accent);

          &:hover {
            background: var(--accent-shade-1);
          }

          &:active {
            background: var(--accent-shade-2);
          }
        }
      }

      .text {
        font-size: 12px;
        margin-bottom: 0;
        margin-top: 4px;
        color: var(--main-dark-light-4);
      }

      .see-all {
        margin-bottom: 0;
        margin-top: var(--spacing-8);
        text-align: center;

        a {
          color: var(--main-light-shade-4);
          text-decoration: none;
          font-weight: 600;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .plan-card-wrapper {
    .plan-card {
      .counter .square {
        padding: 0;
      }
    }
  }
}

.fa {
  .plan-card-wrapper {
    .plan-card {
      position: relative;

      // .label {
      //   background: var(--second-platform-accent);
      //   color: var(--main-light);
      //   padding: var(--spacing-4) var(--spacing-14);
      //   border-radius: 3px;
      //   font-weight: 600;
      //   position: absolute;
      //   left: 77px;
      //   top: -17px;
      // }

      .label.loyalty {
        background: var(--second-platform-accent);
      }

      .content {
        .billed {
          .save-label {
            color: var(--accent);
            background: var(--accent-light-2);

            &.trigger,
            &.collection,
            &.referral {
              color: var(--main-purple-dark-1);
              background: var(--main-purple-light-4);
            }
          }
        }
      }

      .footer {
        button {
          border: none;

          &.collection {
            color: var(--main-dark-shade-0);
          }

          // &.referral {
          //   background: var(--accent);
          // }
        }
      }
    }
  }
}
</style>
